import React, { useState, useEffect, useCallback } from "react";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeart,
  faChevronLeft,
  faChevronRight,
  faFilter,
  faXmark,
  faDownload,
  faCalendarCheck,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

const MatchLog = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMatches, setTotalMatches] = useState(0);
  const [matchesThisMonth, setMatchesThisMonth] = useState(0);
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({ startDate: "", endDate: "" });
  const [activeFilters, setActiveFilters] = useState({ startDate: "", endDate: "" });
  const limit = 20;

  const fetchMatches = useCallback(async (page, f) => {
    setLoading(true);
    try {
      const params = { page, limit, status: "accepted" };
      if (f.startDate) params.startDate = f.startDate;
      if (f.endDate) params.endDate = f.endDate;

      const res = await func.get(urls.LIST_CONNECTS_URL, { params });
      if (res?.status === 200) {
        const data = res.data;
        setMatches(data.connections || []);
        setTotalPages(data.totalPages || 1);
        // Total accepted count from statusCounts
        const accepted = data.statusCounts?.accepted || 0;
        setTotalMatches(accepted);

        // Calculate matches this month from returned data (best effort from status counts)
        // Use totalCount from the accepted filter response if available, else from statusCounts
        if (data.totalCount !== undefined) {
          setTotalMatches(data.totalCount);
        }

        // Compute matches this month from the current page data
        const now = new Date();
        const thisMonth = (data.connections || []).filter((c) => {
          if (!c.acceptedAt) return false;
          const d = new Date(c.acceptedAt);
          return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
        }).length;
        setMatchesThisMonth(thisMonth);
      } else {
        toast.error("Error fetching match log");
      }
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
      toast.error("Network error fetching match log");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMatches(currentPage, activeFilters);
  }, [currentPage, activeFilters, fetchMatches]);

  const applyFilters = () => {
    setActiveFilters({ ...filters });
    setCurrentPage(1);
    setShowFilter(false);
  };

  const resetFilters = () => {
    const empty = { startDate: "", endDate: "" };
    setFilters(empty);
    setActiveFilters(empty);
    setCurrentPage(1);
    setShowFilter(false);
  };

  const daysSince = (dateStr) => {
    if (!dateStr) return "—";
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return "Today";
    if (days === 1) return "1 day";
    return `${days} days`;
  };

  const fmt = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const exportCSV = () => {
    if (matches.length === 0) {
      toast.info("No data to export");
      return;
    }
    const headers = ["#", "Sender Phone", "Receiver Phone", "Matched On", "Duration"];
    const rows = matches.map((m, i) => [
      (currentPage - 1) * limit + i + 1,
      m.senderUserId?._phoneNumber || "",
      m.receiverUserId?._phoneNumber || "",
      fmt(m.acceptedAt),
      daysSince(m.acceptedAt),
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `match-log-page-${currentPage}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1).filter(
    (p) => p >= currentPage - 2 && p <= currentPage + 2
  );

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 mt-14 rounded-xl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faHeart} className="text-white text-xl" />
            <div>
              <h1 className="text-2xl font-bold text-white">Match Log</h1>
              <p className="text-white/70 text-sm">All successful matches (accepted connects)</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportCSV}
              className="flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 text-white text-sm rounded-lg transition"
            >
              <FontAwesomeIcon icon={faDownload} />
              Export CSV
            </button>
            <button
              onClick={() => setShowFilter(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 text-white text-sm rounded-lg transition"
            >
              <FontAwesomeIcon icon={faFilter} />
              Filter
            </button>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-xl p-5 shadow flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon icon={faHeart} className="text-pink-500 text-xl" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{totalMatches.toLocaleString()}</p>
              <p className="text-xs text-slate-500 font-medium">Total Matches Made</p>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 shadow flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon icon={faCalendarCheck} className="text-indigo-500 text-xl" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{matchesThisMonth.toLocaleString()}</p>
              <p className="text-xs text-slate-500 font-medium">Matches This Month (this page)</p>
            </div>
          </div>
        </div>

        {/* Active filter chips */}
        {(activeFilters.startDate || activeFilters.endDate) && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="text-white/60 text-xs">Active filters:</span>
            {activeFilters.startDate && (
              <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full">
                From: {fmt(activeFilters.startDate)}
              </span>
            )}
            {activeFilters.endDate && (
              <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full">
                To: {fmt(activeFilters.endDate)}
              </span>
            )}
            <button
              onClick={resetFilters}
              className="bg-white/20 hover:bg-white/30 text-white text-xs px-3 py-1 rounded-full transition flex items-center gap-1"
            >
              <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
              Clear
            </button>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {["#", "Sender Phone", "Receiver Phone", "Matched On", "Duration"].map((h) => (
                    <th
                      key={h}
                      className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 5 }).map((__, j) => (
                        <td key={j} className="px-5 py-3">
                          <div className="h-4 bg-slate-100 rounded animate-pulse" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : matches.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-16 text-center">
                      <FontAwesomeIcon icon={faHeart} className="text-3xl text-slate-200 mb-3 block mx-auto" />
                      <p className="text-slate-400 text-sm">No matches found</p>
                    </td>
                  </tr>
                ) : (
                  matches.map((m, idx) => (
                    <tr key={m._id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-3 text-slate-400 text-xs">
                        {(currentPage - 1) * limit + idx + 1}
                      </td>
                      <td className="px-5 py-3 font-medium text-slate-800">
                        {m.senderUserId?._phoneNumber || "—"}
                      </td>
                      <td className="px-5 py-3 font-medium text-slate-800">
                        {m.receiverUserId?._phoneNumber || "—"}
                      </td>
                      <td className="px-5 py-3 text-slate-600 text-xs whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5">
                          <FontAwesomeIcon icon={faHeart} className="text-pink-400 text-[10px]" />
                          {fmt(m.acceptedAt)}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">
                        {daysSince(m.acceptedAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
              <span className="text-xs text-slate-400">Page {currentPage} of {totalPages}</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded text-slate-400 hover:text-indigo-600 disabled:opacity-30"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
                </button>
                {pages.map((p) => (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`w-7 h-7 rounded text-xs font-medium transition ${
                      p === currentPage ? "bg-indigo-600 text-white" : "text-slate-500 hover:bg-slate-100"
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded text-slate-400 hover:text-indigo-600 disabled:opacity-30"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filter Modal */}
      {showFilter && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold text-slate-800">Filter by Date Range</h2>
              <button onClick={() => setShowFilter(false)} className="text-slate-400 hover:text-slate-600">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Start Date</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">End Date</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={resetFilters}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition"
              >
                Reset
              </button>
              <button
                onClick={applyFilters}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchLog;
